import React, { useEffect, useRef, useState } from "react";
import {
  ShieldCheck,
  Cloud,
  Cpu,
  Database,
  GitBranch,
  Archive,
  ArrowUpRight,
  ArrowRight,
  Menu,
  X,
  ChevronRight,
  Calendar,
  Sparkles,
  Star,
  CheckCircle2,
  FileText,
  HeartPulse,
  Shield,
  Scale,
  Landmark,
  Users,
  GraduationCap,
  Swords,
  Trees,
} from "lucide-react";
import {
  PDF_URL,
  NAV_LINKS,
  TRUST_MARKS,
  CAPABILITIES,
  TERMINAL_LINES,
} from "../data/site";
import Logo from "./Logo";
import Hero3DScene from "./Hero3DScene";
import Capabilities from "./sections/Capabilities";
import MigrationDemo from "./sections/MigrationDemo";
import ComplianceMatrix from "./sections/ComplianceMatrix";
import ProcessTimeline from "./sections/ProcessTimeline";
import TechStack from "./sections/TechStack";
import CaseStudy from "./sections/CaseStudy";
import WhyDelaware from "./sections/WhyDelaware";
import Portfolio from "./sections/Portfolio";
import Leadership from "./sections/Leadership";

const CAP_ICONS = { "001": Cloud, "002": ShieldCheck, "003": GitBranch, "004": Database, "005": Cpu, "006": Archive };

// Smooth count-up animation for hero stats. Triggers on mount.
function useCountUp(target, { duration = 1800, decimals = 0, delay = 0 } = {}) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    let startTs;
    const start = () => {
      const tick = (now) => {
        if (startTs === undefined) startTs = now;
        const p = Math.min(1, (now - startTs) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        setVal(target * eased);
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const to = setTimeout(start, delay);
    return () => {
      clearTimeout(to);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [target, duration, delay]);
  const factor = Math.pow(10, decimals);
  return Math.round(val * factor) / factor;
}

function AnimatedStat({ value, decimals = 0, suffix = "", label, delay = 0 }) {
  const v = useCountUp(value, { decimals, delay });
  const display = decimals
    ? v.toFixed(decimals)
    : Math.round(v).toLocaleString();
  return (
    <div>
      <div className="font-mono text-[10px] text-slate-500 tracking-widest uppercase mb-2">{label}</div>
      <div className="font-mono text-2xl md:text-3xl text-slate-100 tabular-nums">
        {display}<span className="text-cyan-400">{suffix}</span>
      </div>
    </div>
  );
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const utc = now.toISOString().replace("T", " ").split(".")[0] + " UTC";
  return utc;
}

function Nav({ onOpen }) {
  return (
    <nav className="fixed top-0 w-full z-[100] border-b border-slate-800/60 bg-slate-950/70 backdrop-blur-md">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3">
          <Logo className="h-11 md:h-14 w-auto object-contain" />
        </a>

        <div className="hidden md:flex items-center gap-8 font-mono text-[11px] tracking-widest uppercase text-slate-400">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} className="nav-link">{l.label}</a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <a
            href="#contact"
            className="font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400 hover:text-slate-950 transition-colors"
          >
            Initiate
          </a>
        </div>

        <button onClick={onOpen} className="md:hidden p-2 text-cyan-300" aria-label="Open menu">
          <Menu size={18} />
        </button>
      </div>
    </nav>
  );
}

function MobileMenu({ open, onClose }) {
  return (
    <div id="mobile-menu" className={`fixed inset-0 bg-slate-950/98 backdrop-blur-xl z-[105] md:hidden ${open ? "active" : ""}`}>
      <div className="h-14 border-b border-slate-800/60 flex items-center justify-between px-6">
        <span className="font-mono text-[11px] tracking-[0.3em] text-slate-400 uppercase">Menu</span>
        <button onClick={onClose} className="text-cyan-300" aria-label="Close menu"><X size={18} /></button>
      </div>
      <div className="flex flex-col p-8 gap-6">
        {NAV_LINKS.map((l) => (
          <a key={l.href} href={l.href} onClick={onClose} className="text-slate-200 text-xl tracking-tight font-medium">
            {l.label}
          </a>
        ))}
        <a href={PDF_URL} target="_blank" rel="noreferrer" onClick={onClose} className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300 border-t border-slate-800 pt-6">
          Download Capability Statement ↗
        </a>
      </div>
    </div>
  );
}

function Terminal() {
  const [visible, setVisible] = useState([]);
  useEffect(() => {
    let mounted = true;
    let timers = [];
    const runOnce = () => {
      if (!mounted) return;
      setVisible([]);
      TERMINAL_LINES.forEach((line, i) => {
        const t = setTimeout(() => {
          if (mounted) setVisible((v) => [...v, { ...line, i }]);
        }, 400 + i * 380);
        timers.push(t);
      });
      const restart = setTimeout(runOnce, 400 + TERMINAL_LINES.length * 380 + 3500);
      timers.push(restart);
    };
    runOnce();
    return () => {
      mounted = false;
      timers.forEach(clearTimeout);
    };
  }, []);

  const color = (k) =>
    k === "cmd" ? "text-slate-100" : k === "warn" ? "text-amber-400" : k === "ok" ? "text-emerald-400" : "text-cyan-300/90";

  return (
    <div className="panel-solid corner-brackets">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="font-mono text-[10px] tracking-widest text-slate-500 uppercase">eco.sh · ops console</span>
        </div>
        <span className="font-mono text-[10px] text-slate-600">wave-03 / us-gov-east-1</span>
      </div>
      <div className="p-5 font-mono text-[12px] leading-6 h-[340px] overflow-hidden">
        {visible.map((l) => (
          <div key={l.i} className={`terminal-line ${color(l.kind)}`}>{l.text}</div>
        ))}
        <div className="text-cyan-400 flex items-center gap-1 mt-1">
          <span>$</span>
          <span className="w-2 h-4 bg-cyan-400 blink inline-block" />
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section id="top" className="relative pt-28 md:pt-36 pb-16 md:pb-24 px-6 md:px-10 overflow-hidden">
      {/* Radial glow backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 55% at 70% 40%, rgba(56,189,248,0.16) 0%, rgba(56,189,248,0) 60%), radial-gradient(50% 40% at 15% 20%, rgba(56,189,248,0.10) 0%, rgba(56,189,248,0) 60%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Left column - 3D scene */}
        <div className="lg:col-span-7 relative order-1">
          <div className="relative w-full h-[420px] md:h-[600px] lg:h-[680px]">
            <div className="absolute inset-0">
              <Hero3DScene />
            </div>

            {/* Ring gradient overlay */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at 50% 50%, transparent 55%, rgba(2,6,23,0.5) 85%)",
              }}
            />
          </div>
        </div>

        {/* Right column - CTAs & stats */}
        <div className="lg:col-span-5 relative z-10 order-2">
          <div className="inline-flex items-center gap-2 mb-8 px-3 py-1.5 border border-cyan-400/25 bg-cyan-400/5 rounded-full">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-cyan-200">
              For Delaware Agencies &amp; DoD Contractors
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 hero-reveal hero-reveal-4">
            <a
              href="#contact"
              className="group relative inline-flex items-center justify-center gap-2 px-6 py-4 bg-cyan-400 text-slate-950 text-sm font-semibold tracking-tight rounded-md shadow-[0_0_0_1px_rgba(56,189,248,0.4),0_10px_40px_-8px_rgba(56,189,248,0.55)] hover:bg-white hover:shadow-[0_0_0_1px_rgba(56,189,248,0.6),0_14px_48px_-6px_rgba(56,189,248,0.7)] transition-all duration-300"
            >
              <Calendar size={16} strokeWidth={2} />
              Book a 20-min discovery call
              <ArrowRight size={16} strokeWidth={2} className="transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href={PDF_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-slate-700 text-slate-200 text-sm font-medium tracking-tight rounded-md hover:border-cyan-400/60 hover:text-cyan-200 hover:bg-cyan-400/5 transition-all"
            >
              <FileText size={16} strokeWidth={2} />
              Capabilities (PDF)
              <ArrowUpRight size={14} />
            </a>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500 hero-reveal hero-reveal-5">
            <span className="inline-flex items-center gap-1.5"><Star size={12} className="text-amber-400 fill-amber-400" /> 4.9 client rating</span>
            <span className="text-slate-700">/</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-emerald-400" /> SAM.gov registered</span>
            <span className="text-slate-700">/</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-cyan-400" /> Cleared personnel</span>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 border-t border-slate-800 pt-6 hero-reveal hero-reveal-6">
            <AnimatedStat value={99.987} decimals={3} suffix="%" label="Uptime achieved" delay={400} />
            <AnimatedStat value={47} label="Waves shipped" delay={550} />
            <AnimatedStat value={2195} label="Controls mapped" delay={700} />
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const list = [...TRUST_MARKS, ...TRUST_MARKS];
  return (
    <section className="relative border-y border-slate-800/70 py-6 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center gap-6">
        <span className="font-mono text-[10px] tracking-[0.3em] text-slate-500 uppercase whitespace-nowrap hide-mobile">Frameworks</span>
        <div className="h-px w-8 bg-slate-800 hide-mobile" />
        <div className="flex-1 overflow-hidden">
          <div className="marquee whitespace-nowrap">
            {list.map((m, i) => (
              <span key={i} className="font-mono text-[11px] tracking-[0.2em] text-slate-300 uppercase">
                <span className="text-cyan-400 mr-2">✦</span>{m}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function IndustriesBar() {
  const industries = [
    { icon: HeartPulse, label: "Health & Medicaid" },
    { icon: Shield, label: "Public Safety" },
    { icon: Scale, label: "Judiciary" },
    { icon: Landmark, label: "Revenue & DMV" },
    { icon: Users, label: "Human Services" },
    { icon: GraduationCap, label: "Education" },
    { icon: Swords, label: "Defense · DoD" },
    { icon: Trees, label: "Environment" },
  ];
  return (
    <section id="industries" className="relative border-b border-slate-800/60 py-10 md:py-14 px-6 md:px-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="eyebrow mb-2">Sectors we operate in</div>
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-slate-50 leading-snug">
              A cross-sector firm. Not a single-vertical shop.
            </h2>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed max-w-md">
            Delaware agencies and DoD contract vehicles across eight domains rely on the same disciplined delivery model &mdash; regardless of the workload underneath.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          {industries.map((i) => (
            <div
              key={i.label}
              className="flex items-center gap-2 border border-slate-800 bg-slate-950/60 hover:border-cyan-400/40 hover:bg-slate-900/60 px-3 py-2.5 transition-colors"
            >
              <i.icon size={14} strokeWidth={1.6} className="text-cyan-300 shrink-0" />
              <span className="text-[12px] text-slate-100 font-medium tracking-tight">{i.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ index, kicker, title, description, id }) {
  return (
    <div id={id} className="grid md:grid-cols-12 gap-8 mb-16 items-end reveal">
      <div className="md:col-span-4 flex items-start gap-4">
        <span className="section-index mt-1">/ {index}</span>
        <div>
          <div className="eyebrow mb-2">{kicker}</div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 leading-tight">{title}</h2>
        </div>
      </div>
      <div className="md:col-span-6 md:col-start-7">
        <p className="text-slate-400 text-sm md:text-base leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

function Mission() {
  return (
    <section className="relative py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-[1400px] mx-auto">
        <SectionHeader
          id="mission"
          index="10"
          kicker="Operating Doctrine"
          title="Deliberate work at the edge of the public interest."
          description="Every engagement is scoped against a single-page threat model, a documented control mapping, and an explicit rollback posture. We do not ship code we cannot revert or defend."
        />
        <div className="grid md:grid-cols-3 gap-6 reveal">
          {[
            { k: "Locality", v: "Delaware-domiciled", d: "Small business status pending confirmation. Prime capable within the state." },
            { k: "Model", v: "Fixed outcomes", d: "Fixed-fee outcomes with explicit acceptance criteria. Time-and-materials on advisory only." },
            { k: "Access", v: "Cleared personnel", d: "Personnel eligible for Public Trust and Secret. IL5-eligible on subcontract." },
          ].map((c) => (
            <div key={c.k} className="panel hover-lift p-6">
              <div className="eyebrow-muted mb-4">{c.k}</div>
              <div className="text-xl text-slate-100 font-medium tracking-tight mb-3">{c.v}</div>
              <div className="text-sm text-slate-400 leading-relaxed">{c.d}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    e.target.reset();
  };
  return (
    <section id="contact" className="relative py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-[1400px] mx-auto">
        <SectionHeader
          index="11"
          kicker="Encryption Endpoint"
          title="Open a secure channel."
          description="Contracting officers, prime integrators, and agency CIOs — we respond within one business day. Sensitive material can be routed via encrypted email upon request."
        />
        <div className="grid md:grid-cols-12 gap-10 reveal">
          <div className="md:col-span-5 space-y-10">
            <div>
              <div className="eyebrow-muted mb-3">Direct Line</div>
              <div className="text-2xl md:text-3xl font-mono text-slate-100 tracking-tight">1-800-201-8582</div>
            </div>
            <div>
              <div className="eyebrow-muted mb-3">Encrypted Email</div>
              <div className="text-xl md:text-2xl font-mono text-slate-100 tracking-tight">support@eco-sweeps.com</div>
            </div>
            <div>
              <div className="eyebrow-muted mb-3">Headquarters</div>
              <div className="text-sm text-slate-300 leading-relaxed">
                800 N. King Street, Suite 300<br />
                Wilmington, Delaware 19801<br />
                United States
              </div>
            </div>
            <div className="border-t border-slate-800 pt-6 grid grid-cols-2 gap-6">
              <div>
                <div className="eyebrow-muted mb-2">UEI</div>
                <div className="font-mono text-sm text-slate-300">Pending</div>
              </div>
              <div>
                <div className="eyebrow-muted mb-2">CAGE Code</div>
                <div className="font-mono text-sm text-slate-300">Pending</div>
              </div>
              <div>
                <div className="eyebrow-muted mb-2">NAICS</div>
                <div className="font-mono text-sm text-slate-300">541512 · 541519</div>
              </div>
              <div>
                <div className="eyebrow-muted mb-2">SAM.gov</div>
                <div className="font-mono text-sm text-slate-300">Registered</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <form onSubmit={onSubmit} className="panel corner-brackets p-8 md:p-10 space-y-6">
              <div className="flex items-center justify-between">
                <span className="eyebrow">Transmission form</span>
                <span className="font-mono text-[10px] text-slate-500 tracking-widest">TLS 1.3 · AES-256</span>
              </div>
              {['Full name', 'Agency or organization', 'Email'].map((ph, idx) => (
                <div key={idx} className="group">
                  <label className="eyebrow-muted block mb-2">{ph}</label>
                  <input
                    type={idx === 2 ? "email" : "text"}
                    required
                    className="w-full bg-transparent border-b border-slate-700 focus:border-cyan-400 focus:outline-none py-2 text-slate-100 text-sm transition-colors"
                  />
                </div>
              ))}
              <div>
                <label className="eyebrow-muted block mb-2">Scope of modernization</label>
                <textarea
                  required
                  rows={4}
                  className="w-full bg-transparent border-b border-slate-700 focus:border-cyan-400 focus:outline-none py-2 text-slate-100 text-sm resize-none transition-colors"
                />
              </div>
              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-cyan-400 text-slate-950 font-mono text-[12px] uppercase tracking-[0.3em] hover:bg-white transition-colors"
              >
                {submitted ? "Transmission received ✓" : "Initiate protocol"}
                {!submitted && <ArrowUpRight size={14} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="relative border-t border-slate-800/70 bg-slate-950 mt-16">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <Logo className="h-14 w-auto object-contain" />
          </div>
          <p className="mt-5 text-sm text-slate-400 max-w-md leading-relaxed">
            Technical modernization for Delaware state agencies and Department of Defense contract vehicles. Small business, precisely scoped.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Cloud", "Security", "DevSecOps", "Data", "Compliance"].map((t) => (
              <span key={t} className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400 border border-slate-800 px-3 py-1">
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="eyebrow-muted mb-4">Navigate</div>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.href}><a className="text-sm text-slate-300 hover:text-cyan-300 transition-colors" href={l.href}>{l.label}</a></li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="eyebrow-muted mb-4">Registrations</div>
          <ul className="space-y-3 font-mono text-[11px] text-slate-400">
            <li>SAM.gov</li>
            <li>DE-DUNS</li>
            <li>NAICS 541512</li>
            <li>NAICS 541519</li>
            <li>NAICS 541611</li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="eyebrow-muted mb-4">Contact</div>
          <div className="text-sm text-slate-300 leading-relaxed">
            1-800-201-8582<br />
            support@eco-sweeps.com<br />
            Wilmington, DE
          </div>
          <a
            href={PDF_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-cyan-300 link-underline"
          >
            Capability statement <ArrowUpRight size={12} />
          </a>
        </div>
      </div>
      <div className="border-t border-slate-800/70">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row justify-between gap-3">
          <div className="font-mono text-[10px] tracking-[0.3em] text-slate-500 uppercase">
            © 2026 Eco Sweeps LLC · All rights reserved
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-slate-500 uppercase">
            Public release · Distribution A
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function EcoSweeps() {
  const [menuOpen, setMenuOpen] = useState(false);
  const cursorRef = useRef(null);
  const canvasContainerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let cleanupFn = () => {};

    const waitFor = (predicate, timeout = 8000) =>
      new Promise((resolve, reject) => {
        const start = Date.now();
        const tick = () => {
          if (predicate()) return resolve();
          if (Date.now() - start > timeout) return reject(new Error("timeout"));
          setTimeout(tick, 60);
        };
        tick();
      });

    const start = async () => {
      try {
        await waitFor(() => window.THREE && window.gsap && window.ScrollTrigger);
      } catch (e) {
        // scripts unavailable - continue without animations
      }
      if (cancelled) return;

      const THREE = window.THREE;
      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger;
      if (!THREE || !gsap) return;
      if (ScrollTrigger && gsap.registerPlugin) gsap.registerPlugin(ScrollTrigger);

      // Subtle particle field
      const container = canvasContainerRef.current;
      if (!container) return;
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
      camera.position.z = 90;
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      container.appendChild(renderer.domElement);

      const count = window.innerWidth < 768 ? 500 : 1200;
      const geo = new THREE.BufferGeometry();
      const pos = new Float32Array(count * 3);
      for (let i = 0; i < count * 3; i++) pos[i] = (Math.random() - 0.5) * 260;
      geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      const mat = new THREE.PointsMaterial({
        size: 0.9,
        color: 0x38bdf8,
        transparent: true,
        opacity: 0.45,
        blending: THREE.AdditiveBlending,
      });
      const particles = new THREE.Points(geo, mat);
      scene.add(particles);

      let mouseX = 0, mouseY = 0;
      const handleMouseMove = (e) => {
        mouseX = (e.clientX - window.innerWidth / 2) * 0.008;
        mouseY = (e.clientY - window.innerHeight / 2) * 0.008;
        if (cursorRef.current) {
          gsap.to(cursorRef.current, { x: e.clientX - 10, y: e.clientY - 10, duration: 0.18 });
        }
      };
      window.addEventListener("mousemove", handleMouseMove);

      let animationId;
      const animate = () => {
        animationId = requestAnimationFrame(animate);
        particles.rotation.y += 0.0004;
        particles.rotation.x += (mouseY - particles.rotation.x) * 0.02;
        particles.rotation.z += (mouseX - particles.rotation.z) * 0.02;
        renderer.render(scene, camera);
      };
      animate();

      const handleResize = () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      };
      window.addEventListener("resize", handleResize);

      // Reveal animations - subtle
      const revealEls = gsap.utils.toArray(".reveal");
      revealEls.forEach((el) => {
        gsap.from(el, {
          scrollTrigger: { trigger: el, start: "top 88%" },
          opacity: 0,
          y: 30,
          duration: 0.9,
          ease: "power3.out",
        });
      });

      // Cursor expansions on hover of interactive elements only
      const interactive = document.querySelectorAll("a, button, input, textarea");
      const onEnter = () => gsap.to(cursorRef.current, { scale: 2.4, borderColor: "rgba(56,189,248,0.6)", duration: 0.25 });
      const onLeave = () => gsap.to(cursorRef.current, { scale: 1, borderColor: "rgba(56,189,248,1)", duration: 0.25 });
      interactive.forEach((el) => {
        el.addEventListener("mouseenter", onEnter);
        el.addEventListener("mouseleave", onLeave);
      });

      cleanupFn = () => {
        cancelAnimationFrame(animationId);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("resize", handleResize);
        interactive.forEach((el) => {
          el.removeEventListener("mouseenter", onEnter);
          el.removeEventListener("mouseleave", onLeave);
        });
        if (renderer && container && renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
        if (renderer) renderer.dispose();
        if (ScrollTrigger) ScrollTrigger.getAll().forEach((t) => t.kill());
      };
    };

    start();
    return () => { cancelled = true; cleanupFn(); };
  }, []);

  return (
    <>
      <div className="cursor" ref={cursorRef} />
      <div id="canvas-container" ref={canvasContainerRef} />
      <div className="grid-overlay" />

      <Nav onOpen={() => setMenuOpen(true)} />
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      <main className="relative z-[1]">
        <Hero />
        <TrustStrip />
        <IndustriesBar />
        <WhyDelaware SectionHeader={SectionHeader} />
        <Portfolio SectionHeader={SectionHeader} />
        <Capabilities icons={CAP_ICONS} data={CAPABILITIES} SectionHeader={SectionHeader} />
        <MigrationDemo SectionHeader={SectionHeader} />
        <ComplianceMatrix SectionHeader={SectionHeader} />
        <ProcessTimeline SectionHeader={SectionHeader} />
        <TechStack SectionHeader={SectionHeader} />
        <Leadership SectionHeader={SectionHeader} />
        <CaseStudy SectionHeader={SectionHeader} />
        <Mission />
        <Contact />
      </main>

      <Footer />
    </>
  );
}
